import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLogin } from '../hooks/useAuth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { loginFormSchema, type LoginFormValues } from '@/schemas/auth';
import { Resolver } from 'react-hook-form';
import { User, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

const Login = () => {
  const login = useLogin();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema) as Resolver<LoginFormValues>,
    defaultValues: {
      email: localStorage.getItem('rememberMe') || '',
      password: '',
      rememberMe: localStorage.getItem('rememberMe') ? true : false,
    },
    mode: 'onChange',
  });

  function onSubmit(values: LoginFormValues) {
    if (values.rememberMe) {
      localStorage.setItem('rememberMe', values.email);
    } else {
      localStorage.removeItem('rememberMe');
    }
    login.mutate(values);
  }

  return (
    <div className="bg-gradient-to-r from-luxury-500 to-luxury-700 min-h-screen flex items-center justify-center">
      <div className="absolute inset-0 bg-pattern-overlay opacity-10"></div>
      <div className="container mx-auto max-w-md relative z-10">
        <div className="flex flex-col justify-center items-center">
          <div className="w-full px-8 py-10 rounded-xl bg-white/90 backdrop-blur-sm shadow-xl border border-luxury-200">
            <div className="flex flex-col items-center mb-8">
              <div className="w-16 h-16 luxury-icon-container mb-4">
                <User className="w-8 h-8 text-luxury-600" />
              </div>
              <h1 className="text-5xl font-black tracking-wider text-center bg-gradient-to-r from-luxury-300 via-luxury-400 to-luxury-500 bg-clip-text text-transparent drop-shadow-[0_6px_6px_rgba(0,0,0,0.4)] relative animate-pulse">
                <span className="absolute inset-0 bg-gradient-to-r from-luxury-200/40 via-luxury-300/40 to-luxury-400/40 blur-2xl -z-10"></span>
                AI DAY CRM
              </h1>
              <div className="w-16 h-0.5 bg-luxury-500 mt-2"></div>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="w-full space-y-5"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-luxury-900 font-medium">
                        Email
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Email"
                          type="text"
                          {...field}
                          className="luxury-input"
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-luxury-900 font-medium">
                        Mật khẩu
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Mật khẩu"
                            type={showPassword ? 'text' : 'password'}
                            {...field}
                            className="luxury-input pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent luxury-icon-highlight"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="rememberMe"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0 mt-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="luxury-checkbox"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-medium text-luxury-800">
                          Ghi nhớ tài khoản
                        </FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full mt-6 luxury-button-primary"
                  disabled={login.isPending}
                >
                  {login.isPending ? 'Đang đăng nhập...' : 'Đăng nhập'}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
